import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { isInAppBrowser } from '../utils/browser';
import { syncPushSubscription, subscribePushNotification } from '../utils/pushSubscription';
import { getPushPromptDismissedKey } from '../constants/storageKeys';

/**
 * 알림 켜기 유도 인라인 배너 컴포넌트 (PushPromptBanner)
 * - TempProfileBanner 의 디자인 체계(여백, 모서리, 배경색, 글자 크기, 닫기 버튼)를 계승
 * - 노출 5대 조건:
 *   (가) 푸시 지원 브라우저 (isSupported === true)
 *   (나) 인앱 브라우저 아님 (!isInAppBrowser())
 *   (다) 권한 차단 상태 아님 (permission !== 'denied')
 *   (라) 미구독 상태 (isSubscribed === false)
 *   (마) 해당 userId 로 '나중에' 미선택 (!dismissed)
 *
 * @param {Object} props
 * @param {string} props.userId - 현재 로그인 사용자 ID
 * @param {(msg: string) => void} props.onShowToast - 토스트 알림 호출 콜백
 */
export default function PushPromptBanner({ userId, onShowToast }) {
  const { t } = useTranslation();

  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(true); // 초기에는 true로 두고 확인 후 해제
  const [isBusy, setIsBusy] = useState(false);

  // 1. 화면 진입 시 푸시 상태 및 '나중에' 저장 여부 확인
  useEffect(() => {
    let isMounted = true;

    const checkStatus = async () => {
      // 1) 인앱 브라우저 차단
      if (isInAppBrowser()) {
        if (isMounted) setIsSupported(false);
        return;
      }

      // 2) '나중에' 로컬 스토리지 확인
      try {
        const key = getPushPromptDismissedKey(userId);
        const isDismissed = localStorage.getItem(key) === '1';
        if (isDismissed) {
          if (isMounted) setDismissed(true);
          return;
        }
        if (isMounted) setDismissed(false);
      } catch {
        if (isMounted) setDismissed(false);
      }

      // 3) 푸시 구독 상태 DB 실물 대조 확인 및 조용한 복구
      try {
        const result = await syncPushSubscription(supabase);
        if (!isMounted) return;
        setIsSupported(result.isSupported);
        setPermission(result.permission);
        setIsSubscribed(result.isSubscribed);
      } catch (err) {
        console.warn('[푸시] 배너 푸시 상태 확인 실패:', err);
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  // 5대 조건 검사: 하나라도 만족하지 않으면 미렌더링
  const shouldRender =
    isSupported &&
    !isInAppBrowser() &&
    permission !== 'denied' &&
    !isSubscribed &&
    !dismissed;

  if (!shouldRender) {
    return null;
  }

  // [알림 켜기] 클릭 핸들러 (반드시 사용자 클릭 이벤트 내에서 직접 호출)
  const handleEnablePush = async () => {
    if (isBusy) return;
    setIsBusy(true);

    try {
      const result = await subscribePushNotification(supabase);
      setPermission(result.permission);

      if (result.ok) {
        setIsSubscribed(true);
        onShowToast?.(t('push_prompt_banner.toast_success'));
      } else {
        if (result.reason === 'permission_denied') {
          onShowToast?.(t('push_prompt_banner.toast_denied'));
        } else if (result.reason === 'permission_default') {
          onShowToast?.(t('push_prompt_banner.toast_default'));
        } else if (result.reason === 'sw_timeout') {
          onShowToast?.(t('push_prompt_banner.toast_sw_timeout'));
        } else if (result.reason === 'subscribe_failed' || result.reason === 'vapid_missing') {
          onShowToast?.(t('push_prompt_banner.toast_failed'));
        }
      }
    } catch (err) {
      console.error('[푸시] 배너 알림 켜기 예외 발생:', err);
      onShowToast?.(t('push_prompt_banner.toast_failed'));
    } finally {
      setIsBusy(false);
    }
  };

  // [나중에] / [닫기 X] 클릭 핸들러
  const handleDismiss = () => {
    setDismissed(true);
    try {
      const key = getPushPromptDismissedKey(userId);
      localStorage.setItem(key, '1');
    } catch {
      // 프라이빗 브라우징 접근 불가 대비
    }
  };

  return (
    <div
      role="region"
      aria-label={t('push_prompt_banner.title')}
      className="flex-shrink-0 mx-3 my-2 bg-primary/10 border border-primary/15 rounded-xl px-3.5 py-2.5 flex items-start justify-between gap-3 select-none transition-colors"
    >
      {/* [A] 좌측 아이콘 + 본문 텍스트 + 버튼 영역 */}
      <div className="flex items-start gap-2.5 min-w-0 flex-1">
        {/* 고정 폭 아이콘 컨테이너 */}
        <div className="relative w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center mt-0.5">
          <span
            className="material-symbols-outlined text-[24px] leading-none text-primary opacity-80 select-none absolute left-[11px] top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          >
            notifications
          </span>
        </div>

        {/* 텍스트 및 버튼 컬럼 */}
        <div className="flex flex-col items-start min-w-0 flex-1 pl-3.5">
          <span className="text-on-surface text-[13px] font-semibold leading-snug break-keep break-words">
            {t('push_prompt_banner.title')}
          </span>
          <span className="text-on-surface-variant text-[12px] font-normal leading-relaxed mt-0.5 break-keep break-words">
            {t('push_prompt_banner.desc')}
          </span>

          {/* 버튼 영역 */}
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            <button
              type="button"
              disabled={isBusy}
              onClick={handleEnablePush}
              className="bg-primary text-on-primary text-[12px] font-semibold px-3 py-1 rounded-lg hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
            >
              {t('push_prompt_banner.btn_enable')}
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={handleDismiss}
              className="bg-surface-container-high/60 text-on-surface-variant text-[12px] font-medium px-2.5 py-1 rounded-lg hover:bg-surface-container-highest active:scale-95 transition-all cursor-pointer flex-shrink-0"
            >
              {t('push_prompt_banner.btn_later')}
            </button>
          </div>
        </div>
      </div>

      {/* [B] 닫기(X) 버튼 */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('push_prompt_banner.close_aria')}
        className="w-7 h-7 -mr-1 -mt-0.5 flex items-center justify-center flex-shrink-0 rounded-full text-on-surface-variant/70 hover:text-on-surface hover:bg-primary/10 transition-colors cursor-pointer"
      >
        <span className="material-symbols-outlined text-[15px] leading-none" aria-hidden="true">
          close
        </span>
      </button>
    </div>
  );
}
